/**
 * Cognito SSO configuration, derived from build-time VITE_COGNITO_* env vars.
 *
 * SSO is opt-in: when the authority and client ID are present the app renders
 * the "Sign in with Google" flow; otherwise it falls back to the legacy
 * runtime shared-token screen. None of these values are secrets — a public SPA
 * OIDC client uses Authorization Code + PKCE with no client secret.
 */

export interface CognitoConfig {
  authority: string;
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  scope: string;
  /** Cognito identity_provider hint so the Hosted UI goes straight to Google. */
  idpIdentifier: string;
  /** Hosted UI origin, used to build the Cognito /logout URL. */
  hostedUi: string;
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

// import.meta.env is injected by Vite at build time but is undefined under the
// Node test runner. Read it defensively so importing this module never throws
// outside a Vite build.
function readEnv(): Partial<ImportMetaEnv> {
  return (import.meta.env ?? {}) as Partial<ImportMetaEnv>;
}

export function getCognitoConfig(): CognitoConfig | null {
  const env = readEnv();
  const authority = env.VITE_COGNITO_AUTHORITY?.trim();
  const clientId = env.VITE_COGNITO_CLIENT_ID?.trim();
  if (!authority || !clientId) return null;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return {
    authority: trimTrailingSlash(authority),
    clientId,
    redirectUri: env.VITE_COGNITO_REDIRECT_URI?.trim() || origin,
    postLogoutRedirectUri: env.VITE_COGNITO_LOGOUT_URI?.trim() || origin,
    scope: env.VITE_COGNITO_SCOPE?.trim() || 'openid email profile',
    idpIdentifier: env.VITE_COGNITO_IDP?.trim() || 'Google',
    hostedUi: trimTrailingSlash(env.VITE_COGNITO_HOSTED_UI?.trim() || ''),
  };
}

export function isSsoEnabled(): boolean {
  return getCognitoConfig() !== null;
}

/**
 * Build the Cognito Hosted UI logout URL. Cognito's OIDC discovery document
 * does not expose a standard end_session_endpoint, so we construct /logout
 * explicitly to fully clear the Cognito session (not just the local one).
 */
export function buildLogoutUrl(config: CognitoConfig): string | null {
  if (!config.hostedUi) return null;
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.postLogoutRedirectUri,
  });
  return `${config.hostedUi}/logout?${params.toString()}`;
}
